import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { getCurrentPhase, getTrackingMode } from '../utils/cycleCalculator';
import phasesData from '../data/phases.json';
import allPhasesData from '../data/allPhases.json';
import moonPhasesData from '../data/moonPhases.json';

const ScenarioDetail = () => {
  const { scenarioType } = useParams();
  const navigate = useNavigate();
  const [currentPhase, setCurrentPhase] = useState(null);
  const [activities, setActivities] = useState([]);
  const [isPersonalized, setIsPersonalized] = useState(false);
  const [trackingMode, setTrackingMode] = useState(null);
  const [isPremium, setIsPremium] = useState(false);

  useEffect(() => {
    // Check premium status
    const premiumStatus = localStorage.getItem('isPremium') === 'true';
    setIsPremium(premiumStatus);

    const mode = getTrackingMode();
    setTrackingMode(mode);

    // Try to get personalized phase first
    const personalizedPhase = getCurrentPhase();
    if (personalizedPhase) {
      let phaseData = null;
      if (mode === 'moon') {
        // Find matching moon phase data
        phaseData = moonPhasesData.phases.find(p => 
          p.moonPhase === personalizedPhase.moonPhase
        );
      } else if (mode === 'cycle') {
        // Find matching cycle phase data
        phaseData = allPhasesData.phases.find(p => 
          p.menstrualPhase === personalizedPhase.menstrualPhase
        );
      }

      if (phaseData && phaseData.scenarios[scenarioType]) {
        setCurrentPhase({ ...phaseData, ...personalizedPhase });
        setActivities(phaseData.scenarios[scenarioType]);
        setIsPersonalized(true);
      } else {
        // Fallback to demo data
        const phase = phasesData.phases[0];
        setCurrentPhase(phase);
        if (phase && phase.scenarios[scenarioType]) {
          setActivities(phase.scenarios[scenarioType]);
        }
        setIsPersonalized(false);
      }
    } else {
      // Use demo data
      const phase = phasesData.phases[0];
      setCurrentPhase(phase);
      if (phase && phase.scenarios[scenarioType]) {
        setActivities(phase.scenarios[scenarioType]);
      }
      setIsPersonalized(false);
    }
  }, [scenarioType]);

  const scenarioLabels = {
    business: 'Running a business',
    creation: 'In creation mode',
    kids: 'Kids with you today',
    diary: 'Full diary',
    spiritual: 'Spiritual practice'
  };

  const handleUnlock = () => {
    navigate('/unlock');
  };

  const handlePremiumDashboard = () => {
    navigate('/premium-dashboard');
  };

  if (!currentPhase || !activities.length) return null;

  const getPersonalizedText = () => {
    if (!isPersonalized) return 'DEMO';
    if (trackingMode === 'moon') {
      return `LUNAR DAY ${currentPhase.lunarDay}`;
    } else if (trackingMode === 'cycle') {
      return `CYCLE DAY ${currentPhase.cycleDay}`;
    }
    return 'PERSONALIZED';
  };

  const getCycleDayText = () => {
    if (!isPersonalized) return null;
    if (trackingMode === 'cycle' && currentPhase.cycleDay) {
      return `Personalized for day ${currentPhase.cycleDay} of your cycle`;
    } else if (trackingMode === 'moon' && currentPhase.lunarDay) {
      return `Aligned with lunar day ${currentPhase.lunarDay}`;
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-white px-6 py-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="max-w-md mx-auto"
      >
        {/* Header */}
        <div className="text-center mb-8">
          <button
            onClick={() => navigate('/')}
            className="text-sm text-gray-500 mb-4 hover:text-black transition-colors"
          >
            ← Back
          </button>
          <h1 className="italiana-font text-2xl text-black mb-2">
            {scenarioLabels[scenarioType]} during {currentPhase.name}
          </h1>
          {getCycleDayText() && (
            <p className="text-sm text-gold font-medium">
              {getCycleDayText()}
            </p>
          )}
        </div>

        {/* Show different content based on premium status */}
        {isPremium ? (
          <>
            {/* Premium User - Show Complete Actions */}
            <div className="mb-8">
              <h2 className="italiana-font text-xl text-black mb-6 text-center">
                YOUR COMPLETE {getPersonalizedText()} GUIDANCE:
              </h2>
              
              <div className="space-y-4">
                {activities.map((action, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    className="bg-gray-50 p-4 rounded-lg"
                  >
                    <div className="flex items-start space-x-3">
                      <div className="gold-bg text-white rounded-full w-7 h-7 flex items-center justify-center text-sm font-semibold flex-shrink-0 mt-1">
                        {index + 1}
                      </div>
                      <p className="text-sm leading-relaxed text-gray-800">
                        {action}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Premium Actions */}
            <div className="space-y-4">
              <motion.button
                onClick={handlePremiumDashboard}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full gold-bg text-white py-4 px-6 rounded-lg font-medium hover:opacity-90 transition-opacity"
              >
                📅 View All Scenarios & Calendar
              </motion.button>
              
              <button
                onClick={() => navigate('/')}
                className="w-full text-gray-500 py-2 text-sm hover:text-black transition-colors"
              >
                Try Another Scenario
              </button>
            </div>
          </>
        ) : (
          <>
            {/* Non-Premium User - Show Limited Content + Paywall */}
            <div className="mb-8">
              <h2 className="italiana-font text-xl text-black mb-6 text-center">
                YOUR {getPersonalizedText()} ACTION:
              </h2>

              {/* First Action - Always Shown */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="bg-gray-50 p-6 rounded-lg mb-8"
              >
                <div className="flex items-start space-x-3">
                  <div className="gold-bg text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-semibold flex-shrink-0 mt-1">
                    1
                  </div>
                  <div>
                    <p className="text-sm leading-relaxed text-gray-800">
                      {activities[0]}
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* Paywall CTA */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="text-center space-y-6"
              >
                {/* Teaser for More Actions */}
                <div className="bg-gold bg-opacity-10 p-6 rounded-lg">
                  <h3 className="font-semibold text-black mb-3">
                    Want 7+ More Actions for This Scenario?
                  </h3>
                  <div className="space-y-2 text-sm text-gray-700 text-left">
                    <div className="flex items-start space-x-2">
                      <span className="gold-accent">•</span>
                      <span>Get complete step-by-step guidance for your exact situation</span>
                    </div>
                    <div className="flex items-start space-x-2">
                      <span className="gold-accent">•</span>
                      <span>Smart chat coaching: "This doesn't fit my life" → get alternatives</span>
                    </div>
                    <div className="flex items-start space-x-2">
                      <span className="gold-accent">•</span>
                      <span>Create your secure account with data sync</span>
                    </div>
                    <div className="flex items-start space-x-2">
                      <span className="gold-accent">•</span>
                      <span>All life scenarios: work, relationships, health, travel, finances</span>
                    </div>
                  </div>
                </div>

                {/* Social Proof */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm italic text-gray-600 text-center mb-2">
                    "I used to fight my cycle. Now I use it to 2x my productivity and plan my best months ahead."
                  </p>
                  <p className="text-xs text-gray-500 text-center">
                    — Maria K., Entrepreneur
                  </p>
                </div>

                {/* Main CTA */}
                <button
                  onClick={handleUnlock}
                  className="w-full gold-bg text-white py-4 px-6 rounded-lg font-medium hover:opacity-90 transition-opacity"
                >
                  Get Complete {trackingMode === 'moon' ? 'Lunar' : 'Cycle'} Guidance System →
                </button>

                {/* Secondary Actions */}
                <div className="space-y-3">
                  <button
                    onClick={() => navigate('/')}
                    className="w-full text-gray-500 py-2 text-sm hover:text-black transition-colors"
                  >
                    Try Another Scenario First
                  </button>
                  <p className="text-xs text-gray-500 text-center">
                    🔒 Join 2,847+ women using their cycles as their secret weapon
                  </p>
                </div>
              </motion.div>
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default ScenarioDetail;